// eslint-disable-next-line import/no-internal-modules
import { t } from '@insided/deprecated-libs/translationsUtils/translations'

export interface DefaultAttachment {
  attachmentId: number
  source: string
  source_thumb: string
  type: string
  file_original_name: string
  file_size: number
  file_uuid: string
  url: string
  filename: string
}

export interface EnrichedSavedAttachment extends SavedAttachment {
  filename: string
  url: string
}

export interface EnrichedImageAttachment extends ImageAttachment {
  filename: string
  url: string
}

export interface EnrichedFileAttachment extends FileAttachment {
  filename: string
  url: string
}

export type AttachmentType = DefaultAttachment | EnrichedSavedAttachment | EnrichedImageAttachment | EnrichedFileAttachment

export interface SavedAttachment {
  content_id: string
  content_type: string
  context: string
  file_original_name: string
  file_size: number
  file_uuid: string | null
  short_thumb_url: string
  short_url: string
  source: string
  source_thumb: string
  type: string
}

export interface ImageAttachment {
  short_url: string
  short_thumb_url: string
  type: string
}

export interface FileAttachment {
  filename?: string
  type: string
  source: string
  source_thumb: string
  file_uuid: string | null
  file_size: number
  file_original_name: string
}

export interface UploadedImage {
  bbcode_url: string
  short_thumb_url: string
  short_url: string
  source: string
  source_thumb: string
  type: string
}

export interface ImageSource {
  source: string
  source_thumb: string
}

export interface SignedFile {
  download_url: string
  filename: string
  url: string
  uuid: string
}

export interface AttachmentError {
  filename: string
  errorPhrase: string
}

const allowedFileTypes = [
  'zip',
  'pdf',
  'xml',
  'txt',
  'csv',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'jpg',
  'jpeg',
  'gif',
  'png'
]

export const allowedFilesError = {
  allowed_files_type: allowedFileTypes.join(', '),
  allowed_files_size: '5 Mb'
}

export function normalizeAttachments (attachments: AttachmentType[]) {
  attachments.forEach((attachment) => {
    if ('file_original_name' in attachment && attachment.file_original_name === '') {
      attachment.file_original_name = attachment.source
      attachment.filename = attachment.source
    }
  })
  return attachments
}

export function checkAttachments (hasFileInProgress: boolean) {
  if (hasFileInProgress) {
    throw {
      response: {
        data: {
          errors: {
            pagetext: t('Common', 'file.upload.in.progress')
          }
        }
      }
    }
  }
}

export function prepareAttachments (attachments: AttachmentType[]): Array<(ImageAttachment | FileAttachment)> {
  return attachments.map(attachment => {
    if (attachment.type === 'image') {
      return {
        short_url: ('source' in attachment ? attachment.source : '').split('/').pop() ?? '',
        short_thumb_url: ('source_thumb' in attachment ? attachment.source_thumb : '').split('/').pop() ?? '',
        type: attachment.type
      }
    }
    return {
      filename: attachment.filename,
      type: attachment.type,
      source: 'source' in attachment ? attachment.source : '',
      source_thumb: 'source_thumb' in attachment ? attachment.source_thumb : '',
      file_uuid: 'file_uuid' in attachment ? attachment.file_uuid : '',
      file_size: 'file_size' in attachment ? attachment.file_size : 0,
      file_original_name: 'file_original_name' in attachment ? attachment.file_original_name : ''
    }
  })
}

export function prepareAttachmentsForState (attachments: SavedAttachment[] | Array<ImageAttachment | FileAttachment>) {
  return attachments.map(attachment => ({
    ...attachment,
    url: 'short_url' in attachment ? attachment.short_url : attachment.source,
    filename: 'file_original_name' in attachment ? attachment.file_original_name : ''
  }))
}
